/**
 * Error type for every failed API call. The generated client throws the raw
 * response body; the interceptor in ./client.ts wraps it in ApiError so
 * callers (and TanStack Query) always get a status code and a readable message.
 */

/** Platform-level block codes the backend returns as 403 `detail.code`. */
export const PLATFORM_BLOCK_CODES = ["USER_DEACTIVATED", "PLATFORM_SUSPENDED", "LICENSE_EXPIRED"] as const;
export type PlatformBlockCode = (typeof PLATFORM_BLOCK_CODES)[number];

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    public readonly body?: unknown,
    /** Backend X-Request-ID: quote it in bug reports; it links to backend logs. */
    public readonly requestId: string | null = null,
    /** `Retry-After` seconds on a 429/503: the server asks to wait and try again, not to give up. */
    public readonly retryAfterSeconds: number | null = null,
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = "ApiError";
  }

  private get rawDetail(): unknown {
    return (this.body as { detail?: unknown } | undefined)?.detail;
  }

  /**
   * Human-readable message from FastAPI's error shapes:
   * `{detail: "msg"}`, `{detail: {code, message}}`, or `{detail: [{msg}, ...]}` (422).
   */
  get detail(): string | null {
    const detail = this.rawDetail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      const msgs = detail.map((d) => (d && typeof d === "object" && "msg" in d ? String(d.msg) : "")).filter(Boolean);
      return msgs.length ? msgs.join("; ") : null;
    }
    if (detail && typeof detail === "object" && "message" in detail) return String(detail.message);
    return null;
  }

  /**
   * Structured error code from `{detail: {code}}`, e.g. "USER_DEACTIVATED", `{detail: {error_code}}` (attachment
   * uploads, e.g. "file_too_large"), or a top-level `{code}` (custom exception handlers, e.g. the 429
   * `budget_exceeded`).
   */
  get code(): string | null {
    const detail = this.rawDetail;
    if (detail && typeof detail === "object") {
      if ("code" in detail) return String(detail.code);
      if ("error_code" in detail) return String(detail.error_code);
    }
    const top = (this.body as { code?: unknown } | undefined)?.code;
    return typeof top === "string" ? top : null;
  }

  /** Why a file isn't available yet, from `{detail: {reason_code}}` (e.g. "STARTER_UNAVAILABLE"). */
  get reasonCode(): string | null {
    const detail = this.rawDetail;
    return detail && typeof detail === "object" && "reason_code" in detail ? String(detail.reason_code) : null;
  }

  get platformBlock(): PlatformBlockCode | null {
    const code = this.code;
    return this.status === 403 && PLATFORM_BLOCK_CODES.includes(code as PlatformBlockCode)
      ? (code as PlatformBlockCode)
      : null;
  }
}

/** `Retry-After` in seconds (the delta-seconds form; the backend never sends an HTTP date). */
export function retryAfterSeconds(headers: Headers): number | null {
  const value = headers.get("retry-after")?.trim();
  return value && /^\d+$/.test(value) ? Number.parseInt(value, 10) : null;
}

/** Best message to show a user for any thrown value. */
export function errorMessage(error: unknown, fallback = "Something went wrong."): string {
  if (error instanceof ApiError) return error.detail ?? fallback;
  return fallback;
}
